import React, { createContext, useContext, useEffect, useState } from "react";
import api from "./api";
import { toast } from "sonner";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// Fetch today's follow-ups + overdue count for the current user and surface
// as a soft toast. Runs on login and on page reload if the token is still valid.
async function pingReminderToast() {
  try {
    const { data } = await api.get("/reminders/today");
    const parts = [];
    if (data.total_overdue) parts.push(`${data.total_overdue} overdue`);
    if (data.total_today)   parts.push(`${data.total_today} due today`);
    if (parts.length === 0) return;
    const msg = `Follow-ups — ${parts.join(" · ")}`;
    if (data.total_overdue) toast.error(msg,   { duration: 6000 });
    else                    toast.warning(msg, { duration: 5000 });
  } catch {
    // Reminder banner is best-effort — never block the session on it.
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) { setLoading(false); return; }
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      // Delay the reminder toast so it doesn't collide with initial page paints.
      setTimeout(pingReminderToast, 800);
    }).catch(() => {
      localStorage.removeItem("token");
    }).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    setTimeout(pingReminderToast, 800);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch { /* clear the local session even if the API call fails */ }
    localStorage.removeItem("token");
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, loading, login, logout }}>{children}</AuthCtx.Provider>;
}

const CurrencyCtx = createContext(null);
export const useCurrency = () => useContext(CurrencyCtx);

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => localStorage.getItem("currency") || "INR");
  const set = (c) => { setCurrency(c); localStorage.setItem("currency", c); };
  const symbol = currency === "INR" ? "₹" : "$";
  const rate = currency === "USD" ? 0.012 : 1; // rough INR->USD for display when toggling
  const format = (v) => {
    if (v == null) return "—";
    const val = currency === "USD" ? v * rate : v;
    const opts = currency === "INR" ? { maximumFractionDigits: 0 } : { maximumFractionDigits: 2 };
    return `${symbol}${new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", opts).format(val)}`;
  };
  return <CurrencyCtx.Provider value={{ currency, set, symbol, format }}>{children}</CurrencyCtx.Provider>;
}
